import React from 'react';
import { motion } from 'framer-motion';
import { Target, TrendingUp, PieChart, Receipt, Calculator, Crosshair } from 'lucide-react';
import type { DashboardMetrics, ArgentinaMetrics, ArgentinaInputs, CountryCode } from '../utils/calculations';
import { formatCurrency, formatNumber } from '../utils/calculations';
import { Tooltip } from './Tooltip';
interface MainMetricsProps {
  metrics: DashboardMetrics;
  arMetrics?: ArgentinaMetrics;
  arInputs?: ArgentinaInputs;
  onArChange?: (field: keyof ArgentinaInputs, value: number) => void;
  country: CountryCode;
}
export function MainMetrics({
  metrics,
  arMetrics,
  arInputs,
  onArChange,
  country
}: MainMetricsProps) {
  const isAr = country === 'AR' && arMetrics && arInputs && onArChange;
  const profitPositive = isAr ? arMetrics.gananciaNeta >= 0 : metrics.netProfit >= 0;
  const handleCpaInput = (raw: string) => {
    if (!onArChange) return;
    const cleaned = raw.replace(/\./g, '').replace(',', '.').replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    if (!isNaN(num)) {
      onArChange('cpaActual', num);
    } else if (cleaned === '') {
      onArChange('cpaActual', 0);
    }
  };
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.5,
    delay: 0.2
  }} className="flex flex-col gap-4 h-full" data-id="element-190">
      {/* Top Card: CPA Meta vs CPA Costeado (CO) OR Comisiones vs Costos (AR) */}
      <div className="glass-card p-5 group hover:border-cyber-green/30 transition-colors" data-id="element-191">
        <div className="grid grid-cols-2 gap-4" data-id="element-192">
          {isAr ? <>
              {/* Total Comisiones */}
              <div data-id="element-193">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-194">
                  <Receipt className="w-3.5 h-3.5 text-yellow-400" data-id="element-195" />
                  <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-196">
                    Total Comisiones
                  </h3>
                </div>
                <div className="text-2xl font-black text-yellow-400 tracking-tight" data-id="element-197">
                  {formatCurrency(arMetrics.totalComisiones, country)}
                </div>
                <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-198">
                  Impuestos y pasarelas
                </p>
              </div>
              {/* Total Costos + Comisiones */}
              <div data-id="element-199">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-200">
                  <Calculator className="w-3.5 h-3.5 text-salmon" data-id="element-201" />
                  <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-202">
                    Costos Totales
                  </h3>
                  <Tooltip text="Suma de producto, envío y todas las comisiones." data-id="element-203" />
                </div>
                <div className="text-2xl font-black text-salmon tracking-tight" data-id="element-204">
                  {formatCurrency(arMetrics.totalCostosComisiones, country)}
                </div>
                <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-205">
                  Producto + Envío + Comisiones
                </p>
              </div>
            </> : <>
              {/* CPA Meta Ideal */}
              <div data-id="element-206">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-207">
                  <Target className="w-3.5 h-3.5 text-cyber-green" data-id="element-208" />
                  <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-209">
                    CPA Meta
                  </h3>
                </div>
                <div className="text-2xl font-black text-cyber-green tracking-tight" data-id="element-210">
                  {formatCurrency(metrics.cpaIdeal, country)}
                </div>
                <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-211">
                  Le pagas a Meta
                </p>
              </div>
              {/* CPA Costeado */}
              <div data-id="element-212">
                <div className="flex items-center gap-1.5 mb-1" data-id="element-213">
                  <Target className="w-3.5 h-3.5 text-salmon" data-id="element-214" />
                  <h3 className="text-[10px] font-bold tracking-widest text-white/50 uppercase" data-id="element-215">
                    CPA Costeado
                  </h3>
                  <Tooltip text="Costo real de publicidad por cada entrega exitosa, considerando las devoluciones." data-id="element-216" />
                </div>
                <div className="text-2xl font-black text-salmon tracking-tight" data-id="element-217">
                  {formatCurrency(metrics.cpaCosteado, country)}
                </div>
                <p className="text-[9px] text-white/30 font-medium mt-0.5" data-id="element-218">
                  Te cuesta por entrega
                </p>
              </div>
            </>}
        </div>
        <p className="text-[10px] text-white/20 font-medium mt-3 pt-3 border-t border-white/5 text-center" data-id="element-219">
          {isAr ? 'Resumen de costos operativos antes de publicidad' : 'Lo que le pagas a Meta vs. lo que te cuesta cada entrega real'}
        </p>
        {/* ROAS (CO only) */}
        {!isAr && <div className="mt-3 pt-3 border-t border-cyber-green/10" data-id="element-220">
            <div className="flex items-center gap-2 mb-1" data-id="element-221">
              <TrendingUp className="w-3.5 h-3.5 text-cyber-green/60" data-id="element-222" />
              <h4 className="text-xs font-bold tracking-widest text-cyber-green/60 uppercase" data-id="element-223">
                ROAS
              </h4>
              <Tooltip text="Este es el ROAS que debes ver en Meta para alcanzar tu utilidad neta objetivo." data-id="element-224" />
            </div>
            <div className="text-3xl font-black text-cyber-green tracking-tight" data-id="element-225">
              {metrics.roasTarget.toFixed(2)}
            </div>
            <p className="text-[10px] text-cyber-green/40 font-medium mt-1" data-id="element-226">
              El ROAS que debes ver en Meta para cumplir tu utilidad.
            </p>
          </div>}
      </div>

      {/* AR: Profit Simulator | CO: Net Profit */}
      {isAr ? <div className={`glass-card p-6 flex-grow flex flex-col justify-center relative overflow-hidden ${profitPositive ? 'border-salmon/30 shadow-glow-salmon' : 'border-danger-red/30 shadow-glow-red'}`} data-id="element-227">
          <div className={`absolute top-0 right-0 w-32 h-32 ${profitPositive ? 'bg-salmon/10' : 'bg-danger-red/10'} rounded-full blur-3xl -mr-10 -mt-10`} data-id="element-228"></div>
          <div className="relative z-10" data-id="element-229">
            {/* CPA Input */}
            <div className="mb-4" data-id="element-230">
              <div className="flex items-center gap-2 mb-2" data-id="element-231">
                <Crosshair className="w-4 h-4 text-salmon" data-id="element-232" />
                <h3 className="text-[10px] font-bold tracking-widest text-salmon uppercase" data-id="element-233">
                  Tu CPA Actual en Meta
                </h3>
                <Tooltip text="Ingresa el CPA que estás logrando actualmente en Meta Ads para ver tu ganancia real." data-id="element-234" />
              </div>
              <input type="text" inputMode="decimal" value={formatNumber(arInputs.cpaActual, country)} onChange={e => handleCpaInput(e.target.value)} className="w-full text-center bg-white/[0.04] border border-salmon/30 rounded-xl px-4 py-3 text-2xl font-black text-salmon focus:outline-none focus:border-salmon/60 focus:shadow-[0_0_15px_rgba(242,167,167,0.15)] transition-all placeholder:text-white/20" placeholder="0" data-id="element-235" />
            </div>

            {/* Divider */}
            <div className="h-px w-full bg-white/10 mb-4" data-id="element-236"></div>

            {/* Ganancia Neta Real */}
            <div className="flex items-center gap-2 mb-2" data-id="element-237">
              <div className={`w-2 h-2 rounded-full ${profitPositive ? 'bg-cyber-green' : 'bg-danger-red'} animate-pulse`} data-id="element-238"></div>
              <h3 className={`text-sm font-bold tracking-widest uppercase ${profitPositive ? 'text-cyber-green' : 'text-danger-red'}`} data-id="element-239">
                Ganancia Neta Real
              </h3>
            </div>
            <div className={`text-5xl font-black tracking-tight mb-1 ${profitPositive ? 'text-cyber-green' : 'text-danger-red'}`} data-id="element-240">
              {arMetrics.gananciaNeta < 0 ? '-' : ''}
              {formatCurrency(arMetrics.gananciaNeta, country)}
            </div>
            <p className={`text-xs font-medium ${profitPositive ? 'text-cyber-green/50' : 'text-danger-red/50'}`} data-id="element-241">
              {profitPositive ? 'Ganancia por cada venta completada' : '⚠ Pérdida por venta — ajusta tu CPA o costos'}
            </p>

            {/* Margen Real */}
            <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between" data-id="element-242">
              <div className="flex items-center gap-2" data-id="element-243">
                <PieChart className="w-3.5 h-3.5 text-white/50" data-id="element-244" />
                <span className="text-xs font-bold text-white/50 uppercase tracking-wider" data-id="element-245">
                  % Margen Real
                </span>
              </div>
              <span className={`text-lg font-black ${profitPositive ? 'text-cyber-green' : 'text-danger-red'}`} data-id="element-246">
                {arMetrics.margenReal.toFixed(2)}%
              </span>
            </div>
          </div>
        </div> : <>
          <div className={`glass-card p-6 flex-grow flex flex-col justify-center relative overflow-hidden ${profitPositive ? 'border-salmon/30 shadow-glow-salmon' : 'border-danger-red/30 shadow-glow-red'}`} data-id="element-247">
            <div className={`absolute top-0 right-0 w-32 h-32 ${profitPositive ? 'bg-salmon/10' : 'bg-danger-red/10'} rounded-full blur-3xl -mr-10 -mt-10`} data-id="element-248"></div>
            <div className="relative z-10" data-id="element-249">
              <div className="flex items-center gap-2 mb-2" data-id="element-250">
                <div className={`w-2 h-2 rounded-full ${profitPositive ? 'bg-salmon' : 'bg-danger-red'} animate-pulse`} data-id="element-251"></div>
                <h3 className={`text-sm font-bold tracking-widest uppercase ${profitPositive ? 'text-salmon' : 'text-danger-red'}`} data-id="element-252">
                  Utilidad Neta
                </h3>
                <Tooltip text="Ganancia real después de pagar publicidad, producto y logística (Fletes ida y vuelta)." data-id="element-253" />
              </div>
              <div className={`text-5xl font-black tracking-tight mb-1 ${profitPositive ? 'text-white' : 'text-danger-red'}`} data-id="element-254">
                {metrics.netProfit < 0 ? '-' : ''}
                {formatCurrency(metrics.netProfit, country)}
              </div>
              <p className="text-xs text-white/50 font-medium" data-id="element-255">
                {profitPositive ? 'Ganancia por entrega exitosa' : '⚠ Pérdida por entrega — ajusta tus costos'}
              </p>
            </div>
          </div>

          {/* Net Margin (CO only) */}
          <div className="glass-card p-5 flex items-center justify-between" data-id="element-256">
            <div data-id="element-257">
              <div className="flex items-center gap-2 mb-1" data-id="element-258">
                <PieChart className="w-4 h-4 text-white/70" data-id="element-259" />
                <h3 className="text-xs font-bold tracking-widest text-white/60 uppercase" data-id="element-260">
                  Margen Neto
                </h3>
              </div>
              <div className={`text-2xl font-bold tracking-tight ${metrics.netMargin >= 0 ? 'text-white' : 'text-danger-red'}`} data-id="element-261">
                {metrics.netMargin.toFixed(2)}%
              </div>
            </div>
          </div>
        </>}
    </motion.div>;
}